import React, { useState, useEffect, useRef } from "react";
import { Send, User, X } from "lucide-react";
import { supabase } from "../supabaseClient";

export default function ChatWhatsapp({
  destinatarioId,
  senderId,
  nomeDestinatario,
  onClose,
}) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!destinatarioId || !senderId) return;

    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from("mensagens")
        .select("*")
        .or(
          `and(sender_id.eq.${senderId},receiver_id.eq.${destinatarioId}),and(sender_id.eq.${destinatarioId},receiver_id.eq.${senderId})`,
        )
        .order("created_at", { ascending: true });

      if (!error) setMessages(data || []);
    };

    fetchMessages();

    const channel = supabase
      .channel(`chat_${senderId}_${destinatarioId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "mensagens",
        },
        (payload) => {
          const msg = payload.new;
          if (
            (msg.sender_id === senderId &&
              msg.receiver_id === destinatarioId) ||
            (msg.sender_id === destinatarioId && msg.receiver_id === senderId)
          ) {
            setMessages((prev) => [...prev, msg]);
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [destinatarioId, senderId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const { error } = await supabase.from("mensagens").insert([
      {
        sender_id: senderId,
        receiver_id: destinatarioId,
        content: newMessage,
      },
    ]);

    if (!error) setNewMessage("");
  };

  return (
    <div className="flex flex-col w-full max-w-sm rounded-2xl border border-warm-100 bg-white shadow-[var(--shadow-modal)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-warm-100 bg-warm-50 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-rose-400">
            <User size={16} />
          </div>
          <span className="text-sm font-semibold text-warm-900">
            {nomeDestinatario || "Conversa"}
          </span>
        </div>
        <button
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-warm-400 hover:bg-warm-100 hover:text-warm-700 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Mensagens */}
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-4 min-h-[240px] max-h-[380px] bg-warm-50">
        {messages.map((m) => {
          const isSent = m.sender_id === senderId;
          return (
            <div
              key={m.id}
              className={`flex ${isSent ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${
                  isSent
                    ? "rounded-tr-none bg-rose-400 text-white"
                    : "rounded-tl-none bg-white text-warm-800 border border-warm-100"
                }`}
              >
                {m.content}
                <span
                  className={`block text-right text-[10px] mt-1 ${
                    isSent ? "text-white/70" : "text-warm-400"
                  }`}
                >
                  {new Date(m.created_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form
        className="flex items-center gap-2 border-t border-warm-100 bg-white px-3 py-2.5"
        onSubmit={handleSendMessage}
      >
        <input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Digite uma mensagem..."
          className="flex-1 rounded-full border border-warm-200 bg-warm-50 px-4 py-2 text-sm placeholder:text-warm-400 focus:outline-none focus:ring-2 focus:ring-rose-400/30 focus:border-rose-400 transition"
        />
        <button
          type="submit"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-400 text-white hover:brightness-105 transition"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}
